import React, { useState } from 'react';
import SurveyRecordsTable from '../SurveyRecordsTable';
import { EnhanceTableAction, IField, IPatient, ISubmission, ISurvey } from '../../../constants/types';
import { Translate } from 'react-localize-redux';
import * as types from "../../../constants";
import Modal from '../../../components/general/modal';
import { TextField, Typography } from '@mui/material';
import { HOSPITAL_BILLING_EDIT } from '../../../routes/urls';
import { LinkStyled } from '../../../components/general/mini_components';
import { Link } from 'react-router-dom';
import { BillingInfo } from '../Billing/types';
import SubmissionsTableSelector from './SubmissionsTableSelector';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { SubmissionPDFDocument } from '../../../components/pdf/documents/SubmissionPDFDocument';
import { PDFDownloadButtonWithSignature } from '../../../components/pdf/components/PDFDownloadButtonWithSignature';
import { usePDFConfig } from '../../../hooks/usePDFConfig';
import { useDispatch, useSelector } from 'react-redux';
import { getSubmissionPatientService } from '../../../services';
import { extractPrescribingDoctor } from '../../../utils/doctorUtils';
import { useResearcherRole } from '../../../hooks/useResearcherRole';
import { deleteSubmissionPatientAction } from '../../../redux/actions/submissionsPatientActions';
import { canDeletePrescriptionFromTable } from './submissionsTableActions';

interface SubmissionsTableProps {
    locale:string,
    fieldsSurvey?: IField[],
    surveys: ISurvey[],
    records: ISubmission[],
    category: number,
    patient:IPatient,
    billingInfo: BillingInfo | null,
    onSelectSubmission: (index:number) => void;
}

const SubmissionsTable: React.FC<SubmissionsTableProps> = ({ locale, surveys, patient, billingInfo, category, fieldsSurvey, records, 
                                                        onSelectSubmission }) => {
    const dispatch = useDispatch<any>();
    const [showModalPrintPDF, setShowModalPrintPDF] = useState(false);
    const [submissionPrint, setSubmissionPrint] = useState<ISubmission | null>(null);
    const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
    const [deleteReason, setDeleteReason] = useState('');
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const currentInvestigationUuid = useSelector((state: any) =>
        state.investigations.currentInvestigation?.uuid
    );
    const permissions = useSelector((state: any) =>
        state.investigations.currentInvestigation?.permissions ?? []
    );
    
    // Get PDF config and investigation UUID for new PDF component
    const { config: pdfConfig } = usePDFConfig();
    // SHER-224: resolve the uploading researcher's role to label the PDF signature.
    const prescribingDoctorRole = useResearcherRole({
        uuidInvestigation: currentInvestigationUuid || '',
        uuidResearcher: submissionPrint?.uuidResearcher,
        enabled: showModalPrintPDF,
    });

    const canDeletePrescriptions = canDeletePrescriptionFromTable({ category, permissions });

    async function printSubmission(submission:ISubmission){
        // SHER-483 review: the list now holds SLIM rows (no surveyRecords), so printing a PDF
        // straight from the list would render an empty document. If the row is slim, fetch the
        // full submission by id before printing.
        let toPrint = submission;
        if(submission && (submission as any).__slim && currentInvestigationUuid){
            try {
                const resp:any = await getSubmissionPatientService(currentInvestigationUuid, submission.id);
                if(resp?.submission){
                    toPrint = resp.submission;
                }
            } catch (e) { /* fall back to the slim row rather than blocking the print */ }
        }
        setSubmissionPrint(toPrint);
        setShowModalPrintPDF(true);
    }

    function openDeleteModal(index: number) {
        setPendingDeleteIndex(index);
        setDeleteReason('');
        setDeleteError(null);
    }

    function closeDeleteModal() {
        if (deleteLoading) {
            return;
        }
        setPendingDeleteIndex(null);
        setDeleteReason('');
        setDeleteError(null);
    }

    async function confirmDeletePrescription() {
        if (pendingDeleteIndex === null || !currentInvestigationUuid) {
            return;
        }
        const record = records[pendingDeleteIndex];
        if (!record?.id || !record.uuidSurvey) {
            return;
        }
        const trimmedReason = deleteReason.trim();
        if (!trimmedReason) {
            setDeleteError('hospital.patient.submission-table.delete_reason_required');
            return;
        }

        setDeleteLoading(true);
        setDeleteError(null);
        try {
            await dispatch(deleteSubmissionPatientAction(
                { deletionReason: trimmedReason },
                currentInvestigationUuid,
                record.id,
                patient.uuid,
                record.uuidSurvey,
            ));
            setPendingDeleteIndex(null);
            setDeleteReason('');
            setDeleteError(null);
        } catch (error: any) {
            const message = error?.response?.data?.errorMessage
                || error?.message
                || 'hospital.error.general';
            setDeleteError(typeof message === 'string' ? message : 'hospital.error.general');
        } finally {
            setDeleteLoading(false);
        }
    }

    function renderDeleteModal() {
        if (pendingDeleteIndex === null) {
            return null;
        }

        return (
            <Modal
                open
                title={<Translate id="hospital.patient.submission-table.confirm_delete_prescription" />}
                closeModal={closeDeleteModal}
                confirmAction={confirmDeletePrescription}
                confirmButtonLabel="general.delete"
                disabled={deleteLoading}
            >
                <div style={{ minWidth: 320 }}>
                    <Typography variant="body2" style={{ marginBottom: 16 }}>
                        <Translate id="hospital.patient.submission-table.confirm_delete_prescription" />
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={<Translate id="hospital.patient.submission-table.delete_reason_label" />}
                        value={deleteReason}
                        onChange={(event) => setDeleteReason(event.target.value)}
                        disabled={deleteLoading}
                    />
                    {deleteError && (
                        <Typography color="error" variant="body2" style={{ marginTop: 12 }}>
                            {deleteError.startsWith('hospital.') ? <Translate id={deleteError} /> : deleteError}
                        </Typography>
                    )}
                </div>
            </Modal>
        );
    }

    function renderPDFModal(){
        const currentSurvey = surveys.find((survey) => survey.uuid === submissionPrint?.uuidSurvey);
        
        if(!pdfConfig){
            return(
                <Modal key="modal" medium open={showModalPrintPDF} title="" closeModal={() => setShowModalPrintPDF(false)}>
                    <Typography variant='body2'><Translate id="pages.hospital.patient.fill_billing_info" />, <Link to={HOSPITAL_BILLING_EDIT}><Translate id="general.here" /></Link></Typography>
                </Modal>
            )
        }
        else if(currentSurvey && submissionPrint){
            // Find prescribing doctor from the submission
            const prescribingDoctor = extractPrescribingDoctor(submissionPrint);
            
            return(
                <Modal key="modal" medium open={showModalPrintPDF} title="" closeModal={() => setShowModalPrintPDF(false)}>
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <PDFDownloadButtonWithSignature
                            uuidInvestigation={currentInvestigationUuid || ''}
                            uuidResearcher={prescribingDoctor?.uuid}
                            filename={`${currentSurvey.name}-${submissionPrint.id}`}
                            variant="contained"
                            size="large"
                            label={<Translate id="general.download_pdf" />}
                        >
                            {(signatureBase64) => (
                                <SubmissionPDFDocument
                                    submission={submissionPrint}
                                    currentSurvey={currentSurvey}
                                    patient={patient}
                                    prescribingDoctor={prescribingDoctor}
                                    prescribingDoctorRole={prescribingDoctorRole || undefined}
                                    config={pdfConfig}
                                    nameSurvey={currentSurvey.name}
                                    locale={locale}
                                    uuidInvestigation={currentInvestigationUuid || ''}
                                    signatureBase64={signatureBase64}
                                />
                            )}
                        </PDFDownloadButtonWithSignature>
                    </div>
                </Modal>
            )
        }
        
    }
    if(surveys.length > 0 && [types.CATEGORY_DEPARTMENT_NURSE, types.CATEGORY_DEPARTMENT_NURSE_FW].includes(surveys[0].category)){
        return <SubmissionsTableSelector surveys={surveys} records={records} 
                    locale={locale} billingInfo={billingInfo}
                    onSelectSubmission={(index) => onSelectSubmission(index)}  />
    }
        
    let headCells = [{ id: "researcher", alignment: "left", label: <Translate id="hospital.created-by" />}, { id: "surveyName", alignment: "left", label: <Translate id="hospital.data-collection" />},
                        { id: "date", alignment: "left", label: "Date"}];
    let actions:EnhanceTableAction[] = [
        {
            "type" : "pdf", 
            "func" : (index) => printSubmission(records[index]),
            "check": (row: any) => {
                const record = records[row.id];
                return record && (record as any).type !== "stay";
            }
        }
    ];

    if (canDeletePrescriptions) {
        actions.push({
            type: 'delete',
            func: (index) => openDeleteModal(index),
            check: (row: any) => canDeletePrescriptionFromTable({
                category,
                permissions,
                recordType: (records[row.id] as any)?.type,
            }),
        });
    }

    return(
        <>
            {renderPDFModal()}
            {renderDeleteModal()}
            <EnhancedTable noHeader noSelectable selectRow={(index:number) => onSelectSubmission(index)} 
                rows={records.map((record, index) => {
                    const dateCreatedString = record.createdAt ? new Date(record.createdAt).toLocaleString('en-CA', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    }).replace(',', '') : "Unsincronized";
                    return({id : index, researcher : record.researcher, surveyName : record.surveyName, date : dateCreatedString, type: (record as any).type})
                })} headCells={headCells} 
                    actions={actions} /> 
        </>
    );
};

export default SubmissionsTable;
